/**
 * JSON-LD builders for structured data. One source of truth for the SPA.
 * Bots that reach the site (via the deploy's prerender) see these blocks in
 * the <head> because useSeo injects them client-side before the crawler
 * captures the DOM.
 */

import { SOCIAL_URLS } from "@/lib/socials";

export const SITE_ORIGIN = "https://humandesignstudio.nl";
export const PHONE_TEL = "+31646090104";
export const EMAIL = "info@humandesignstudio.nl";
export const OG_IMAGE =
    "https://customer-assets-0z36b82j.emergentagent.net/job_marcel-coaching/artifacts/i4x1ckrv_marcel-strand.jpg";

const ADDRESS = {
    "@type": "PostalAddress",
    streetAddress: "Jupiterweg 19a108",
    postalCode: "8938 AD",
    addressLocality: "Leeuwarden",
    addressRegion: "Friesland",
    addressCountry: "NL",
};

export const organizationLd = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Human Design Studio",
    url: SITE_ORIGIN + "/",
    logo: OG_IMAGE,
    email: EMAIL,
    telephone: PHONE_TEL,
    address: ADDRESS,
    sameAs: SOCIAL_URLS,
    contactPoint: {
        "@type": "ContactPoint",
        telephone: PHONE_TEL,
        contactType: "customer service",
        email: EMAIL,
        areaServed: "NL",
        availableLanguage: "Dutch",
    },
});

export const websiteLd = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Human Design Studio",
    url: SITE_ORIGIN + "/",
    inLanguage: "nl-NL",
});

export const professionalServiceLd = () => ({
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Human Design Studio",
    description:
        "Human Design coaching door Marcel van der Veen. Sessies online, live afspreken in Friesland mogelijk.",
    url: SITE_ORIGIN + "/",
    telephone: PHONE_TEL,
    email: EMAIL,
    image: OG_IMAGE,
    areaServed: ["Nederland", "Friesland"],
    address: ADDRESS,
    availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: SITE_ORIGIN + "/boeken",
        availableLanguage: "Dutch",
    },
});

export const personLd = () => ({
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Marcel van der Veen",
    jobTitle: "Human Design coach",
    worksFor: { "@type": "Organization", name: "Human Design Studio" },
    url: SITE_ORIGIN + "/over-marcel",
    image: OG_IMAGE,
    email: EMAIL,
    telephone: PHONE_TEL,
    address: ADDRESS,
    sameAs: SOCIAL_URLS,
    description:
        "Marcel van der Veen combineert Human Design met NLP, familieopstellingen en lichaamswerk. 25 jaar ervaring in persoonlijke ontwikkeling.",
});

const OFFERS = [
    { name: "HD sessie basis", price: 95, description: "Sessie van 60 minuten." },
    { name: "HD sessie uitgebreid", price: 195, description: "Sessie van 120 minuten." },
    { name: "Verdiepingssessie", price: 95, description: "Losse sessie voor een specifieke vraag." },
    { name: "HD traject", price: 995, description: "Tien sessies in een begeleid traject." },
    { name: "Avond workshop", price: 295, description: "Workshop per persoon." },
];

export const serviceOffersLd = () => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Human Design sessies en trajecten",
    provider: { "@type": "Organization", name: "Human Design Studio", url: SITE_ORIGIN + "/" },
    areaServed: ["Nederland", "Friesland"],
    hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Aanbod",
        itemListElement: OFFERS.map((o) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: o.name, description: o.description },
            price: String(o.price),
            priceCurrency: "EUR",
            url: SITE_ORIGIN + "/aanbod",
            availability: "https://schema.org/InStock",
        })),
    },
});

export const breadcrumbLd = (name, path) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN + "/" },
        { "@type": "ListItem", position: 2, name, item: SITE_ORIGIN + path },
    ],
});

export const blogLd = () => ({
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog | Human Design Studio",
    url: SITE_ORIGIN + "/blog",
    inLanguage: "nl",
    author: { "@type": "Person", name: "Marcel van der Veen" },
});

export const blogPostingLd = (post) => {
    const image = post.featured_image
        ? post.featured_image.startsWith("http")
            ? post.featured_image
            : SITE_ORIGIN + post.featured_image
        : undefined;
    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.seo_description || post.excerpt || "",
        inLanguage: "nl",
        mainEntityOfPage: `${SITE_ORIGIN}/blog/${post.slug}`,
        ...(post.published_at ? { datePublished: post.published_at } : {}),
        ...(post.updated_at ? { dateModified: post.updated_at } : {}),
        ...(image ? { image } : {}),
        author: {
            "@type": "Person",
            name: "Marcel van der Veen",
            url: SITE_ORIGIN + "/over-marcel",
        },
        publisher: { "@type": "Organization", name: "Human Design Studio" },
    };
};

export const blogBreadcrumbLd = (title, slug) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN + "/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: SITE_ORIGIN + "/blog" },
        { "@type": "ListItem", position: 3, name: title, item: `${SITE_ORIGIN}/blog/${slug}` },
    ],
});
